import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { Users, BookOpenCheck, Trophy, Activity, Sparkles, HelpCircle, Filter, TrendingUp,
  UserCheck, ClipboardCheck, ChevronDown, ChevronRight, Loader2, RefreshCw, ShieldCheck,
} from "lucide-react";
import { auth } from "@/lib/api";

const COLORS = ["#16a34a", "#0ea5e9", "#eab308", "#dc2626"];

function daysAgoIso(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function Dashboard() {
  const [filters, setFilters] = useState({
    date_from: daysAgoIso(30),
    date_to: todayIso(),
    location_id: "",
    department_id: "",
    questionnaire_id: "",
  });
  const [kpis, setKpis] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [stats, setStats] = useState(null);
  const [masters, setMasters] = useState({ locations: [], depts: [], quizzes: [] });
  const [loading, setLoading] = useState(false);
  const [openDept, setOpenDept] = useState(null);
  const abortRef = useRef(null);

  const loadMasters = useCallback(async () => {
    try {
      const [loc, d, q] = await Promise.all([
        api.get("/masters/locations"), api.get("/masters/departments"), api.get("/questionnaires"),
      ]);
      setMasters({ locations: loc.data || [], depts: d.data || [], quizzes: q.data || [] });
    } catch { /* ignore */ }
  }, []);

  const loadDashboard = useCallback(async () => {
    // Cancel in-flight request when filters change rapidly.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [k, c, s] = await Promise.all([
        api.get("/dashboard/kpis", { params, signal: controller.signal }),
        api.get("/dashboard/compliance", { params: { ...params, department_id: undefined }, signal: controller.signal }),
        api.get("/dashboard/stats", { signal: controller.signal }).catch(() => ({ data: null })),
      ]);
      setKpis(k.data.kpis);
      setCompliance(c.data.rows);
      setStats(s.data);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return; // superseded
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
        abortRef.current = null;
      }
    }
  }, [filters.date_from, filters.date_to, filters.location_id, filters.department_id, filters.questionnaire_id]);

  useEffect(() => { loadMasters(); }, [loadMasters]);
  // Debounce filter changes so rapid preset clicks don't fire multiple requests.
  useEffect(() => {
    const t = setTimeout(loadDashboard, 250);
    return () => clearTimeout(t);
  }, [loadDashboard]);

  const sessionUser = auth.user();
  const isHod = (sessionUser?.role || "").toLowerCase() === "hod";

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Overview</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl mt-1 text-slate-900" data-testid="dashboard-title">
            {isHod ? "HOD Dashboard" : "Training Dashboard"}
          </h1>
          <p className="text-slate-500 mt-1">
            {isHod ? "Auto-scoped to the employees mapped to you." : "Filter-aware view of training performance across your hospital."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isHod && (
            <Badge variant="secondary" className="rounded-full px-3 py-1.5 bg-violet-50 text-violet-700 border border-violet-200" data-testid="hod-scope-pill">
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5"/>Scoped to your team
            </Badge>
          )}
          <Badge variant="secondary" className="rounded-full px-3 py-1.5 bg-sky-50 text-sky-700 border border-sky-200">
            <Sparkles className="h-3.5 w-3.5 mr-1.5"/>Range: {filters.date_from} → {filters.date_to}
          </Badge>
          <Button variant="outline" size="sm" onClick={loadDashboard} disabled={loading} data-testid="dashboard-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}
          </Button>
        </div>
      </header>

      {/* Filters */}
      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-sky-600"/>
            <div className="font-bold text-sm">Filters</div>
            <div className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">default: last 30 days</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">From</Label>
              <Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="dash-date-from"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">To</Label>
              <Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="dash-date-to"/>
            </div>
            <SelectFilter label="Location" val={filters.location_id} onChange={v => setFilters({ ...filters, location_id: v })} options={masters.locations} tid="dash-location"/>
            <SelectFilter label="Department" val={filters.department_id} onChange={v => setFilters({ ...filters, department_id: v })} options={masters.depts} tid="dash-department"/>
            <SelectFilter label="Quiz / Training" val={filters.questionnaire_id} onChange={v => setFilters({ ...filters, questionnaire_id: v })} options={masters.quizzes} valueKey="id" labelKey="title" tid="dash-quiz"/>
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setFilters({ date_from: daysAgoIso(30), date_to: todayIso(), location_id: "", department_id: "", questionnaire_id: "" })} data-testid="dash-reset-filters">
              Reset to last 30d
            </Button>
            {[7, 30, 90, 180, 365].map(n => (
              <Button key={n} size="sm" variant="ghost" className="text-xs text-slate-500 hover:text-sky-600"
                onClick={() => setFilters({ ...filters, date_from: daysAgoIso(n), date_to: todayIso() })}
                data-testid={`dash-preset-${n}`}>Last {n}d</Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-kpis">
        {loading && !kpis ? (
          <>
            <KpiSkeleton/><KpiSkeleton/><KpiSkeleton/><KpiSkeleton/>
          </>
        ) : (
          <>
            <Kpi icon={Users} label="Employees (scope)" value={kpis?.total_employees_in_scope ?? "—"} sub="in current filter" color="sky" tid="kpi-emp-scope"/>
            <Kpi icon={ClipboardCheck} label="Assigned" value={kpis?.total_assigned ?? "—"} sub={`${kpis?.total_completed ?? 0} completed · ${kpis?.total_pending ?? 0} pending`} color="amber" tid="kpi-assigned"/>
            <Kpi icon={Trophy} label="Compliance" value={`${kpis?.compliance_pct ?? 0}%`} sub={`avg score ${kpis?.avg_score_pct ?? 0}%`} color="rose" tid="kpi-compliance"/>
            <Kpi icon={UserCheck} label="Unique Trained (all-time)" value={kpis?.unique_trained_employees_all_time ?? "—"} sub="since implementation" color="emerald" tid="kpi-unique"/>
          </>
        )}
      </div>

      {/* Training Effectiveness card */}
      <TrainingEffectivenessCard te={kpis?.training_effectiveness}/>

      {/* Compliance table with inline drill-down */}
      <ComplianceTable rows={compliance} openDept={openDept} setOpenDept={setOpenDept} loading={loading}/>

      {/* Existing charts (kept for continuity) */}
      {stats && (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-slate-200 rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-display font-bold text-lg">Quiz Engagement (all-time)</h3>
                <div className="text-xs text-slate-400">attempts × avg score per quiz</div>
              </div>
              {stats.questionnaire_stats.length === 0 ? (
                <div className="h-64 grid place-items-center text-sm text-slate-400">No data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={stats.questionnaire_stats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                    <XAxis dataKey="title" tick={{ fontSize: 11 }} tickFormatter={(v)=>v.length > 20 ? v.slice(0,20)+"…" : v}/>
                    <YAxis tick={{ fontSize: 11 }}/>
                    <Tooltip/>
                    <Bar dataKey="attempts" fill="#0ea5e9" radius={[8,8,0,0]}/>
                    <Bar dataKey="avg_score" fill="#f59e0b" radius={[8,8,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card className="border-slate-200 rounded-2xl">
            <CardContent className="p-6">
              <h3 className="font-display font-bold text-lg mb-2">Score Distribution</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={[
                    { name: "Excellent (≥85%)", value: stats.buckets.excellent, color: COLORS[0] },
                    { name: "Good (70-84%)", value: stats.buckets.good, color: COLORS[1] },
                    { name: "Average (50-69%)", value: stats.buckets.average, color: COLORS[2] },
                    { name: "Poor (<50%)", value: stats.buckets.poor, color: COLORS[3] },
                  ]} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                    {COLORS.map((c, i) => <Cell key={i} fill={c}/>)}
                  </Pie>
                  <Tooltip/>
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function SelectFilter({ label, val, onChange, options, valueKey = "id", labelKey = "name", tid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Select value={val || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger data-testid={tid}><SelectValue placeholder={`All ${label.toLowerCase()}s`}/></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All {label.toLowerCase()}s</SelectItem>
          {options.map((o) => <SelectItem key={o[valueKey]} value={o[valueKey]}>{o[labelKey]}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function TrainingEffectivenessCard({ te }) {
  const has = te && te.n_employees_with_both > 0;
  const delta = te?.improvement_pct ?? 0;
  return (
    <Card className="border-slate-200 rounded-2xl overflow-hidden" data-testid="training-effectiveness-card">
      <CardContent className="p-0">
        <div className="bg-gradient-to-r from-sky-50 via-violet-50 to-emerald-50 p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-white grid place-items-center shadow-sm"><TrendingUp className="h-5 w-5 text-sky-600"/></div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">Training Effectiveness</div>
              <div className="text-sm text-slate-600 mt-0.5">Pre-Assessment vs Post-Assessment</div>
            </div>
          </div>
          {has ? (
            <div className="flex items-center gap-3 flex-wrap">
              <MiniStat label="Avg Pre-Test" value={`${te.avg_pre_pct}%`} color="violet" tid="te-pre"/>
              <div className="text-slate-300 text-2xl font-thin">→</div>
              <MiniStat label="Avg Post-Test" value={`${te.avg_post_pct}%`} color="emerald" tid="te-post"/>
              <div className={`px-4 py-2 rounded-xl font-black text-lg ${delta > 0 ? "bg-emerald-100 text-emerald-700" : delta < 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`} data-testid="te-improvement">
                {delta > 0 ? "+" : ""}{delta}%
              </div>
              <div className="text-[11px] text-slate-500">
                across {te.n_employees_with_both} employee{te.n_employees_with_both === 1 ? "" : "s"} with both attempts
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-400 italic">No employees have completed both Pre and Post tests in the selected range yet.</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value, color, tid }) {
  const map = {
    violet: "bg-violet-100 text-violet-700",
    emerald: "bg-emerald-100 text-emerald-700",
  };
  return (
    <div className="text-center">
      <div className={`px-4 py-2 rounded-xl font-black text-lg ${map[color]}`} data-testid={tid}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1 font-bold">{label}</div>
    </div>
  );
}

function ComplianceTable({ rows, openDept, setOpenDept, loading }) {
  const firstLoad = rows === null;
  const list = rows || [];
  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-0">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg text-slate-900">Training Compliance by Department</div>
            <p className="text-xs text-slate-500 mt-0.5">Click a row to drill into employee-level detail.</p>
          </div>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-sky-500"/>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="compliance-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="w-6 py-3"></th>
                <th className="py-3 px-4">Department</th>
                <th className="py-3 px-4 text-center">Total Emps</th>
                <th className="py-3 px-4 text-center">Assigned</th>
                <th className="py-3 px-4 text-center">Completed</th>
                <th className="py-3 px-4 text-center">Pending</th>
                <th className="py-3 px-4">Compliance %</th>
                <th className="py-3 px-4">Avg Score %</th>
              </tr>
            </thead>
            <tbody>
              {firstLoad && loading && [1, 2, 3].map(i => (
                <tr key={`sk-${i}`} className="border-b border-slate-100 animate-pulse" data-testid="compliance-row-skeleton">
                  <td className="py-3 pl-4"><div className="h-4 w-4 bg-slate-100 rounded"/></td>
                  <td className="py-3 px-4"><div className="h-4 w-32 bg-slate-100 rounded"/></td>
                  {[1, 2, 3, 4, 5, 6].map(j => (
                    <td key={j} className="py-3 px-4"><div className="h-4 w-10 bg-slate-100 rounded mx-auto"/></td>
                  ))}
                </tr>
              ))}
              {!firstLoad && list.length === 0 && !loading && (
                <tr><td colSpan="8" className="py-10 text-center text-slate-400">No employees in current scope.</td></tr>
              )}
              {list.map((r) => {
                const open = openDept === r.department_id;
                return (
                  <Fragment key={r.department_id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpenDept(open ? null : r.department_id)} data-testid={`compliance-row-${r.department_id}`}>
                      <td className="py-3 pl-4 text-slate-400">{open ? <ChevronDown className="h-4 w-4"/> : <ChevronRight className="h-4 w-4"/>}</td>
                      <td className="py-3 px-4 font-bold text-slate-900">{r.department_name}</td>
                      <td className="py-3 px-4 text-center">{r.total_employees}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-slate-700">{r.employees_assigned}</span>
                        <span className="text-slate-300 text-xs"> · {r.assigned_trainings}t</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-emerald-700 font-bold">{r.employees_completed}</span>
                        <span className="text-slate-300 text-xs"> · {r.completed_trainings}t</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-amber-700 font-bold">{r.employees_pending}</span>
                        <span className="text-slate-300 text-xs"> · {r.pending_trainings}t</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          r.compliance_pct >= 85 ? "bg-green-100 text-green-700" :
                          r.compliance_pct >= 60 ? "bg-yellow-100 text-yellow-700" :
                          "bg-red-100 text-red-700"
                        }`}>{r.compliance_pct}%</span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{r.avg_score_pct}%</td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-50/70" data-testid={`compliance-drill-${r.department_id}`}>
                        <td colSpan="8" className="px-6 py-3">
                          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">Employee-level detail</div>
                          <table className="w-full text-xs">
                            <thead className="text-slate-400">
                              <tr>
                                <th className="text-left py-2">Emp ID</th>
                                <th className="text-left py-2">Name</th>
                                <th className="text-left py-2">Designation</th>
                                <th className="text-center py-2">Assigned</th>
                                <th className="text-center py-2">Completed</th>
                                <th className="text-center py-2">Pending</th>
                                <th className="py-2">Avg %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.employees.map((e) => (
                                <tr key={e.id} className="border-t border-slate-200/70">
                                  <td className="py-1.5 font-mono text-sky-700 font-bold">{e.emp_id}</td>
                                  <td className="py-1.5">{e.name}</td>
                                  <td className="py-1.5 text-slate-400">{e.designation || "—"}</td>
                                  <td className="py-1.5 text-center">{e.assigned}</td>
                                  <td className="py-1.5 text-center text-emerald-700 font-bold">{e.completed}</td>
                                  <td className="py-1.5 text-center text-amber-700 font-bold">{e.pending}</td>
                                  <td className="py-1.5">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                      e.avg_score_pct >= 85 ? "bg-green-100 text-green-700" :
                                      e.avg_score_pct >= 50 ? "bg-yellow-100 text-yellow-700" :
                                      e.avg_score_pct > 0 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-400"
                                    }`}>{e.avg_score_pct}%</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Kpi({ icon: Icon, label, value, sub, color, tid }) {
  const map = {
    sky: "bg-sky-50 text-sky-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
  };
  return (
    <Card className="border-slate-200 rounded-2xl group relative">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className={`h-10 w-10 rounded-xl grid place-items-center ${map[color]}`}><Icon className="h-5 w-5"/></div>
          <HelpCircle className="h-4 w-4 text-slate-300"/>
        </div>
        <div className="mt-3 font-display font-black text-3xl text-slate-900" data-testid={tid || `kpi-${label.toLowerCase()}`}>{value}</div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
        <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
      </CardContent>
    </Card>
  );
}

function KpiSkeleton() {
  return (
    <Card className="border-slate-200 rounded-2xl animate-pulse" data-testid="kpi-skeleton">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="h-10 w-10 rounded-xl bg-slate-100"/>
          <div className="h-3 w-3 rounded-full bg-slate-100"/>
        </div>
        <div className="mt-3 h-8 w-24 bg-slate-100 rounded"/>
        <div className="mt-2 h-3 w-32 bg-slate-100 rounded"/>
        <div className="mt-1.5 h-2.5 w-24 bg-slate-100 rounded"/>
      </CardContent>
    </Card>
  );
}
